import { useEffect, useState } from 'react'

import { getPhone } from '$app/utilities'

import useSignal from '$actions/useSignal'
import useMFAInfo from '$actions/useMFAInfo'
import { useRouter } from '$actions/useRouter'

import { profile } from '$store/profile'

import useEmailVerifyRequest from './useEmailVerifyRequest'
import usePhoneVerification from './usePhoneVerification'

type ActionTypeEmail = {
  id: 'email'
  props: Pick<ReturnType<typeof useEmailVerifyRequest>, 'otpTimer'>
}

type ActionTypePhone = {
  id: 'phone'
  props: Pick<
    ReturnType<typeof usePhoneVerification>,
    'modal' | 'otpBlock' | 'cancel'
  >
}

type ActionType2FA = {
  id: '2fa'
  props: undefined
}

type ActionTypes = ActionTypeEmail | ActionTypePhone | ActionType2FA

type Action = {
  title: string
  description: string
  loading: boolean
  onClick: VoidFunction
  actionText: string
  disabled?: boolean
  resMessage?: string
} & ActionTypes

export const useSidebarActions = () => {
  const { is_2fa_enforced, is_2fa_enabled } = useMFAInfo()

  const profileStore = useSignal(profile)
  const {
    email,
    phone_code,
    phone_no,
    is_email_verified,
    is_phone_verified,
    id: currentProfileId
  } = { ...profileStore?.data }
  const [activeIndex, setActiveIndex] = useState(0)

  const emailVerify = useEmailVerifyRequest()
  const phoneVerify = usePhoneVerification()

  const hasPhone = !!getPhone({ phone_code, phone_no })

  const { on, redirect } = useRouter()
  const isMFAPage = on('securityMfa')

  useEffect(() => {
    if (!currentProfileId) return
    resetAction()
  }, [currentProfileId])

  const actions = (() => {
    if (isMFAPage) {
      return []
    }

    const list: Action[] = []

    if (email && !is_email_verified) {
      const { request: onClick, otpTimer, ...rest } = emailVerify

      list.push({
        ...rest,
        id: 'email',
        title: 'Verify Your Email',
        description:
          'By clicking the verify button to proceed the email verification process.',
        onClick,
        actionText: 'Verify',
        resMessage: otpTimer.active
          ? `Verification code is being sent to your email address. (${otpTimer.seconds}s)`
          : undefined,
        props: { otpTimer }
      })
    }

    if (hasPhone && !is_phone_verified) {
      const { loading, request: onClick, ...props } = phoneVerify

      list.push({
        id: 'phone',
        title: 'Verify Your Phone',
        description:
          'By clicking the verify button to proceed the phone verification process.',
        loading,
        onClick,
        actionText: 'Verify',
        props
      })
    }

    if (!is_2fa_enforced && !is_2fa_enabled) {
      list.push({
        id: '2fa',
        title: 'Secure Your Account with 2FA',
        description:
          'Click the Enable 2FA button to add an extra layer of security to your login process.',
        loading: false,
        onClick: () => redirect('securityMfa'),
        actionText: 'Enable 2FA',
        props: undefined
      })
    }

    return list
  })()

  const formattedActions = actions.map((action, index) => ({
    ...action,
    isActive: index === activeIndex
  }))

  const hasActions = !!actions.length
  const hasMultipleActions = actions.length > 1

  const rotateAction = () => {
    setActiveIndex(prevIndex => (prevIndex + 1) % actions.length)
  }

  const resetAction = () => {
    setActiveIndex(0)
  }

  return {
    formattedActions,
    hasActions,
    hasMultipleActions,
    resetAction,
    rotateAction,

    emailVerify,
    phoneVerify
  }
}

export default useSidebarActions
